import * as T from 'three'
import type { BedTrimStyle } from '../../../../../config/blissRoomFinish'

export type TrimRect = {x:number; y:number; w:number; h:number}
export type TrimEdge = 'front' | 'back' | 'left' | 'right'
/** Coordinates in inches. Quarter-round tops out at ¾", below 1" drawer bottoms. */
export function addBedTrim(root:T.Group, r:TrimRect, deck:number, lower:boolean, style:BedTrimStyle | undefined, origin:{width:number;depth:number}, omit:TrimEdge[] = []) {
  if (!style || style==='none') return
  const material=()=>new T.MeshStandardMaterial({color:0xffffff,roughness:0.65})
  const add=(name:string,geometry:T.BufferGeometry,x:number,y:number,z:number,angle=0)=>{
    const mesh=new T.Mesh(geometry,material());mesh.name=name;mesh.userData.finishPart='frame'
    mesh.position.set((x-origin.width/2)/12,y/12,(z-origin.depth/2)/12);mesh.rotation.y=angle;root.add(mesh)
  }
  const cap=(edge:TrimEdge,x:number,z:number,w:number,d:number)=>{
    if(omit.includes(edge))return
    add('bed-cap-'+edge,new T.BoxGeometry(w/12,0.75/12,d/12),x+w/2,deck-0.375,z+d/2)
  }
  // 1×3 flat, ¾" projection. Butt returns avoid stacked, intersecting corners.
  const leftProjection=omit.includes('left')?0:0.75, rightProjection=omit.includes('right')?0:0.75
  cap('front',r.x-leftProjection,r.y+r.h-1.75,r.w+leftProjection+rightProjection,2.5)
  cap('back',r.x-leftProjection,r.y-0.75,r.w+leftProjection+rightProjection,2.5)
  cap('left',r.x-0.75,r.y+1.75,2.5,r.h-3.5)
  cap('right',r.x+r.w-1.75,r.y+1.75,2.5,r.h-3.5)
  if(!lower||style!=='cap_quarter_round')return
  const quarter=(edge:TrimEdge,x:number,z:number,length:number,angle:number,startNeighbor:TrimEdge,endNeighbor:TrimEdge)=>{
    if(omit.includes(edge))return
    const shape=new T.Shape();shape.moveTo(0,0);shape.lineTo(0,0.75/12)
    shape.absarc(0,0,0.75/12,Math.PI/2,0,true);shape.closePath()
    const geometry=new T.ExtrudeGeometry(shape,{depth:length/12,bevelEnabled:false,curveSegments:12})
    const positions=geometry.getAttribute('position'), run=length/12
    // Local X is the profile's outward projection. Extend each connected
    // end by that projection: z=-x and z=run+x are complementary 45° cuts.
    // At an omitted neighboring edge, retain a square termination.
    const startMiter=!omit.includes(startNeighbor), endMiter=!omit.includes(endNeighbor)
    for(let i=0;i<positions.count;i++){
      const outward=positions.getX(i), along=positions.getZ(i), t=along/run
      positions.setZ(i,along-(startMiter?outward*(1-t):0)+(endMiter?outward*t:0))
    }
    positions.needsUpdate=true
    geometry.computeVertexNormals()
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    add('bed-quarter-round-'+edge,geometry,x,0,z,angle)
  }
  quarter('front',r.x+r.w,r.y+r.h,r.w,-Math.PI/2,'right','left')
  quarter('back',r.x,r.y,r.w,Math.PI/2,'left','right')
  quarter('left',r.x,r.y+r.h,r.h,Math.PI,'front','back')
  quarter('right',r.x+r.w,r.y,r.h,0,'back','front')
}
